import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Plus, Trash2, Lock, Save, Copy, Building2, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { PageShell } from "@/components/page-shell";
import { useAuth } from "@/lib/auth";

type Role = {
  name: string;
  description: string | null;
  isSystem: boolean;
  isReadOnly: boolean;
  isCustomized: boolean;
  permissions: string[];
  // null == unrestricted (Super Admin and OrgAdmin both receive null).
  // Platform-only permissions are enforced server-side and excluded from the
  // /api/permissions catalog for non-SA users so they never appear as checkboxes.
  allowedPermissions: string[] | null;
};

type Permission = { name: string; category: string; description: string };

type GlobalModule = {
  category: string;
  displayName: string;
  enabled: boolean;
  permissionCount: number;
};

// Per-tenant module status (from /api/admin/tenant-modules/{orgId})
type TenantModuleStatus = {
  category: string;
  displayName: string;
  globalEnabled: boolean;
  tenantOverride: boolean | null;  // null = no override
  effectiveEnabled: boolean;
  source: "global" | "override";
};

// Capability availability (from /api/capabilities/availability)
type CapabilityInfo = {
  key: string;
  label: string;
  type: "read" | "write" | "delete" | "specialized";
  enabled: boolean;  // globally enabled by SA (module × capability setting)
  permissions: string[];  // underlying permission names
};

type CategoryCapabilities = {
  category: string;
  capabilities: CapabilityInfo[];
};

export default function RolesManagerPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = (user as any)?.role === "super_admin";
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ description: string; permissions: Set<string> } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneDraft, setCloneDraft] = useState({ name: "", description: "" });

  // Tenant selected for per-tenant module overrides (SA only).
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const { data: roles, isLoading: rolesLoading } = useQuery<Role[]>({ queryKey: ["/api/roles"] });
  const { data: perms, isLoading: permsLoading } = useQuery<Permission[]>({ queryKey: ["/api/permissions"] });

  // Global module availability — Super Admin only (coarse module-level gate).
  const { data: globalModules } = useQuery<GlobalModule[]>({
    queryKey: ["/api/admin/global-modules"],
    enabled: isSuperAdmin,
  });

  const moduleMap = useMemo(() => {
    const m = new Map<string, GlobalModule>();
    (globalModules ?? []).forEach(mod => m.set(mod.category, mod));
    return m;
  }, [globalModules]);

  const toggleModule = useMutation({
    mutationFn: ({ category, enabled }: { category: string; enabled: boolean }) =>
      apiRequest("PUT", `/api/admin/global-modules/${encodeURIComponent(category)}`, { enabled }),
    onSuccess: (_d, { category, enabled }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/global-modules"] });
      queryClient.invalidateQueries({ queryKey: ["/api/capabilities/availability"] });
      toast({
        title: `Module ${enabled ? "enabled" : "disabled"}`,
        description: `The "${category}" module is now ${enabled ? "available" : "unavailable"} to all tenant organizations.`,
      });
    },
    onError: (e: any) => toast({ title: "Failed to update module", description: e?.message, variant: "destructive" }),
  });

  // ── Capability availability ──────────────────────────────────────────────
  // The SA sets fine-grained Read/Write/Delete availability per module.
  // OrgAdmin's checkboxes are enabled/disabled based on this map.
  // isLoading = still fetching; isError = fetch failed (e.g. 401, 500, network).
  // A disabled capability is NOT an error — it is a valid enabled:false entry in the response.
  const { data: capabilityData, isLoading: capabilityLoading, isError: capabilityError } =
    useQuery<CategoryCapabilities[]>({ queryKey: ["/api/capabilities/availability"] });

  const capMap = useMemo(() => {
    const m = new Map<string, CategoryCapabilities>();
    (capabilityData ?? []).forEach(c => m.set(c.category, c));
    return m;
  }, [capabilityData]);

  // SA-only: toggle a fine-grained capability (Read/Write/Delete/Specialized).
  const toggleCap = useMutation({
    mutationFn: ({ category, capKey, enabled }: { category: string; capKey: string; enabled: boolean }) =>
      apiRequest("PUT", `/api/admin/global-capabilities/${encodeURIComponent(category)}/${encodeURIComponent(capKey)}`, { enabled }),
    onSuccess: (_d, { capKey, enabled }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/capabilities/availability"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/global-capabilities"] });
      toast({ title: `Capability ${enabled ? "enabled" : "disabled"}`, description: `"${capKey}" is now ${enabled ? "available" : "unavailable"} to tenant organizations.` });
    },
    onError: (e: any) => toast({ title: "Failed to update capability", description: e?.message, variant: "destructive" }),
  });

  // ── Tenant list (SA only — to populate the tenant selector) ─────────────
  // Reuses the /api/admin/stats payload which the Tenants tab already fetches;
  // with the shared react-query cache there is no extra network request.
  const { data: statsData } = useQuery<any>({
    queryKey: ["/api/admin/stats"],
    enabled: isSuperAdmin,
  });
  const tenantList: { id: string; name: string }[] = useMemo(() => {
    return (statsData?.organizations ?? [])
      .filter((o: any) => o.slug !== "platform")
      .map((o: any) => ({ id: o.id, name: o.name }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [statsData]);

  // ── Per-tenant module override states (loaded when SA selects a tenant) ──
  const { data: tenantModules, refetch: refetchTenantModules } = useQuery<TenantModuleStatus[]>({
    queryKey: [`/api/admin/tenant-modules/${selectedTenantId}`],
    enabled: isSuperAdmin && !!selectedTenantId,
    queryFn: async () => {
      const token = getAuthToken();
      const res = await fetch(`/api/admin/tenant-modules/${selectedTenantId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const tenantModuleMap = useMemo(() => {
    const m = new Map<string, TenantModuleStatus>();
    (tenantModules ?? []).forEach(t => m.set(t.category, t));
    return m;
  }, [tenantModules]);

  // ── Set/clear a per-tenant module override ───────────────────────────────
  const toggleTenantModule = useMutation({
    mutationFn: async ({ category, enabled }: { category: string; enabled: boolean | null }) => {
      const token = getAuthToken();
      const res = await fetch(
        `/api/admin/tenant-modules/${selectedTenantId}/${encodeURIComponent(category)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!res.ok) throw new Error("Failed to update tenant module override");
    },
    onSuccess: (_d, { category, enabled }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/tenant-modules/${selectedTenantId}`] });
      const tenantName = tenantList.find(t => t.id === selectedTenantId)?.name ?? "tenant";
      toast({
        title: "Tenant override updated",
        description: enabled === null
          ? `Override cleared for "${category}" — ${tenantName} follows the global setting.`
          : `"${category}" ${enabled ? "enabled" : "disabled"} for ${tenantName}.`,
      });
    },
    onError: (e: any) => toast({ title: "Failed to update tenant override", description: e?.message, variant: "destructive" }),
  });

  // SA in global context (no org): sees the Admin role in the list.
  // SA in org context / OrgAdmin: Admin role is not in the list.
  const isGlobalSA = isSuperAdmin && (roles ?? []).some(r => r.name === "Admin");

  // ── Capability helpers (OrgAdmin read/write/delete view) ─────────────────
  // A capability is "checked" when ALL its underlying permissions are granted.
  const isCapChecked = (capPerms: string[]) =>
    capPerms.length > 0 && capPerms.every(p => draft?.permissions.has(p) ?? false);

  // A capability is "indeterminate" when some (not all) underlying permissions are granted.
  const isCapIndeterminate = (capPerms: string[]) => {
    if (!draft) return false;
    const n = capPerms.filter(p => draft.permissions.has(p)).length;
    return n > 0 && n < capPerms.length;
  };

  // Toggle all underlying permissions for a capability on/off.
  const toggleCaps = (capPerms: string[], on: boolean, capEnabled: boolean) => {
    if (!draft || !capEnabled) return;
    const next = new Set(draft.permissions);
    if (on) capPerms.forEach(p => next.add(p));
    else capPerms.forEach(p => next.delete(p));
    setDraft({ ...draft, permissions: next });
  };

  // Auto-select the first role when data lands; never depend on `selected` here
  // so we don't re-trigger on our own setSelected call.
  useEffect(() => {
    if (!roles || roles.length === 0) return;
    setSelected(prev => (prev && roles.find(r => r.name === prev) ? prev : roles[0].name));
  }, [roles]);

  // Reseed the draft whenever the selected role (or its server copy) changes.
  useEffect(() => {
    if (!roles || !selected) return;
    const role = roles.find(r => r.name === selected);
    if (role) setDraft({ description: role.description ?? "", permissions: new Set(role.permissions) });
  }, [roles, selected]);

  const current = roles?.find(r => r.name === selected) ?? null;
  const isDirty = useMemo(() => {
    if (!current || !draft) return false;
    if ((current.description ?? "") !== draft.description) return true;
    if (current.permissions.length !== draft.permissions.size) return true;
    return current.permissions.some(p => !draft.permissions.has(p));
  }, [current, draft]);

  const grouped = useMemo(() => {
    if (!perms) return [] as { category: string; items: Permission[] }[];
    const map = new Map<string, Permission[]>();
    perms.forEach(p => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    });
    return Array.from(map, ([category, items]) => ({ category, items }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [perms]);

  const save = useMutation({
    mutationFn: async () => {
      if (!current || !draft) return;
      await apiRequest("PATCH", `/api/roles/${encodeURIComponent(current.name)}`, {
        description: draft.description,
        permissions: Array.from(draft.permissions),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Role saved" });
    },
    onError: (e: any) => toast({ title: "Failed to save role", description: e?.message, variant: "destructive" }),
  });

  const create = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/roles", { name: newRole.name.trim(), description: newRole.description, permissions: [] }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      setCreateOpen(false);
      setNewRole({ name: "", description: "" });
      setSelected(data?.name ?? newRole.name.trim());
      toast({ title: "Role created" });
    },
    onError: (e: any) => toast({ title: "Failed to create role", description: e?.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: async (name: string) => apiRequest("DELETE", `/api/roles/${encodeURIComponent(name)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      setSelected(null);
      toast({ title: "Role deleted" });
    },
    onError: (e: any) => toast({ title: "Failed to delete role", description: e?.message, variant: "destructive" }),
  });

  const reset = useMutation({
    mutationFn: async (name: string) => apiRequest("POST", `/api/roles/${encodeURIComponent(name)}/reset`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      toast({ title: "Role reset to defaults" });
    },
    onError: (e: any) => toast({ title: "Failed to reset role", description: e?.message, variant: "destructive" }),
  });

  const clone = useMutation({
    mutationFn: async ({ source, name, description }: { source: string; name: string; description: string }) =>
      apiRequest("POST", `/api/roles/${encodeURIComponent(source)}/clone`, { name: name.trim(), description: description.trim() || undefined }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      setCloneOpen(false);
      setCloneDraft({ name: "", description: "" });
      setSelected(data?.name);
      toast({ title: "Role cloned" });
    },
    onError: (e: any) => toast({ title: "Failed to clone role", description: e?.message, variant: "destructive" }),
  });

  // ── Permission edit helpers ───────────────────────────────────────────────
  // null allowed set == unrestricted (Super Admin editing the global default).
  const allowedSet = useMemo(
    () => (current?.allowedPermissions ? new Set(current.allowedPermissions) : null),
    [current],
  );
  // A permission is locked (checkbox disabled) when the role is read-only, or when the
  // Super Admin controls it — i.e. it falls outside the caller's allowed whitelist.
  const isLocked = (name: string) =>
    (current?.isReadOnly ?? false) || (allowedSet !== null && !allowedSet.has(name));

  const toggleAll = (category: string, on: boolean) => {
    if (!draft || !perms) return;
    const next = new Set(draft.permissions);
    perms
      .filter(p => p.category === category && !isLocked(p.name))
      .forEach(p => (on ? next.add(p.name) : next.delete(p.name)));
    setDraft({ ...draft, permissions: next });
  };
  const toggleOne = (name: string) => {
    if (!draft || isLocked(name)) return;
    const next = new Set(draft.permissions);
    next.has(name) ? next.delete(name) : next.add(name);
    setDraft({ ...draft, permissions: next });
  };

  const loading = rolesLoading || permsLoading;
  const readOnly = current?.isReadOnly ?? false;

  const selectedTenantName = tenantList.find(t => t.id === selectedTenantId)?.name;

  return (
    <PageShell
      title={<span className="flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /> Roles & Permissions</span>}
      subtitle="Pick a role to view or edit which permissions it grants."
      titleTestId="text-roles-manager-title"
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="button-new-role">
          <Plus className="w-4 h-4 mr-1" /> New role
        </Button>
      }
    >
      {/* ── SA: tenant selector for per-tenant module overrides ─────────────
          Displayed only to Super Admins. Selecting a tenant causes the module
          availability section to show that tenant's override state alongside
          the global toggle so SA can configure both in one place. */}
      {isSuperAdmin && (
        <Card className="mb-4">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Building2 className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-tight">Tenant module overrides</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select a tenant to view and configure its per-tenant module availability alongside the global settings below.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select
                  value={selectedTenantId ?? "__none__"}
                  onValueChange={v => setSelectedTenantId(v === "__none__" ? null : v)}
                >
                  <SelectTrigger className="w-56 h-8 text-sm" data-testid="select-tenant-module-override">
                    <SelectValue placeholder="Select tenant…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground">No tenant selected</span>
                    </SelectItem>
                    {tenantList.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTenantId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setSelectedTenantId(null)}
                    title="Clear tenant selection"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <Card className="lg:sticky lg:top-4 self-start">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Roles ({roles?.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : (
              <div className="space-y-1" data-testid="list-roles">
                {(roles ?? []).map(r => (
                  <button
                    key={r.name}
                    type="button"
                    onClick={() => setSelected(r.name)}
                    data-active={r.name === selected}
                    data-testid={`button-role-${r.name}`}
                    className="w-full text-left px-3 py-2 rounded-md text-sm hover-elevate active-elevate-2 data-[active=true]:bg-accent data-[active=true]:text-accent-foreground"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{r.name}</span>
                      {r.isReadOnly ? <Lock className="w-3 h-3 opacity-60" />
                        : r.isSystem ? <Badge variant="secondary" className="text-[9px]">built-in</Badge>
                        : <Badge variant="outline" className="text-[9px]">custom</Badge>}
                    </div>
                    {r.description && <div className="text-[11px] text-muted-foreground truncate">{r.description}</div>}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{r.permissions.length} permission{r.permissions.length === 1 ? "" : "s"}</div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4 min-w-0">
          {!current ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Select a role to view its permissions.</CardContent></Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {current.name}
                        {current.isReadOnly
                          ? <Badge variant="secondary">read-only</Badge>
                          : current.isSystem
                          ? <Badge variant="secondary">built-in</Badge>
                          : <Badge variant="outline">custom</Badge>}
                        {current.isCustomized && <Badge variant="outline" className="text-[10px]">customized</Badge>}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {current.isCustomized && !readOnly && (
                        <Button variant="outline" size="sm" onClick={() => reset.mutate(current.name)} disabled={reset.isPending} data-testid="button-reset-role">
                          Reset to defaults
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => { setCloneDraft({ name: `${current.name} (copy)`, description: current.description ?? "" }); setCloneOpen(true); }} data-testid="button-clone-role">
                        <Copy className="w-3.5 h-3.5 mr-1" /> Clone
                      </Button>
                      {!current.isSystem && !current.isReadOnly && (
                        <Button variant="destructive" size="sm" onClick={() => del.mutate(current.name)} disabled={del.isPending} data-testid="button-delete-role">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      )}
                      {!readOnly && (
                        <Button size="sm" onClick={() => save.mutate()} disabled={!isDirty || save.isPending} data-testid="button-save-role">
                          <Save className="w-3.5 h-3.5 mr-1" /> Save
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="role-description" className="text-xs text-muted-foreground">Description</Label>
                  <Input
                    id="role-description"
                    value={draft?.description ?? ""}
                    onChange={e => setDraft(d => d ? { ...d, description: e.target.value } : d)}
                    disabled={readOnly}
                    placeholder="Describe what this role is for…"
                    className="mt-1"
                    data-testid="input-role-description"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">Permissions</CardTitle>
                    <CardDescription>
                      {isGlobalSA
                        ? `${(perms ?? []).filter(p => draft?.permissions.has(p.name)).length} of ${perms?.length ?? 0} granted · Configure capability availability for tenant organizations below`
                        : `${(perms ?? []).filter(p => draft?.permissions.has(p.name)).length} of ${perms?.length ?? 0} granted`}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  {grouped.map(group => {
                    const granted  = group.items.filter(i => draft?.permissions.has(i.name)).length;
                    const toggleable = group.items.filter(i => !isLocked(i.name));
                    const all = toggleable.length > 0 && toggleable.every(i => draft?.permissions.has(i.name));
                    const mod = moduleMap.get(group.category);
                    const moduleEnabled = mod?.enabled ?? true;
                    const catCaps = capMap.get(group.category);
                    // Per-tenant override state for this category (if a tenant is selected).
                    const tenantMod = tenantModuleMap.get(group.category);

                    return (
                      <div key={group.category}>
                        {/* ── Category header ── */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                            {group.category}
                          </div>
                          {/* Select all / count — only when SA shows raw perms */}
                          {isGlobalSA && (readOnly || toggleable.length === 0 ? (
                            <span className="text-[11px] text-muted-foreground" data-testid={`text-granted-${group.category}`}>
                              {granted}/{group.items.length}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleAll(group.category, !all)}
                              className="text-[11px] text-primary hover:underline"
                              data-testid={`button-toggle-all-${group.category}`}
                            >
                              {all ? "Clear all" : "Select all"} ({granted}/{group.items.length})
                            </button>
                          ))}
                        </div>

                        {/* ── SA only: global tenant availability control ── */}
                        {isSuperAdmin && mod && (
                          <div className={`mb-3 rounded-lg border p-3 transition-colors ${
                            moduleEnabled
                              ? "border-green-200 bg-green-50/40 dark:border-green-900/40 dark:bg-green-950/20"
                              : "border-red-200 bg-red-50/40 dark:border-red-900/40 dark:bg-red-950/20"
                          }`}>
                            {/* Toggle + status row */}
                            <div className="flex items-center gap-3">
                              <Switch
                                id={`module-toggle-${group.category}`}
                                checked={moduleEnabled}
                                onCheckedChange={(checked) =>
                                  toggleModule.mutate({ category: group.category, enabled: checked })
                                }
                                disabled={toggleModule.isPending}
                                aria-label={`Enable or disable the ${group.category} module for all tenant organizations`}
                                title="Enable/disable this module for all tenants."
                                data-testid={`switch-module-${group.category}`}
                              />
                              <div>
                                <div className={`flex items-center gap-1.5 text-[13px] font-semibold leading-tight ${
                                  moduleEnabled
                                    ? "text-green-700 dark:text-green-400"
                                    : "text-red-700 dark:text-red-400"
                                }`}>
                                  <span aria-hidden>{moduleEnabled ? "🟢" : "🔴"}</span>
                                  {moduleEnabled ? "Enabled for tenants" : "Disabled for tenants"}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  Global · {moduleEnabled
                                    ? "Tenants can access this module"
                                    : "Tenants cannot access this module"}
                                </div>
                              </div>
                            </div>

                            {/* Capability chips — fine-grained Read/Write/Delete controls.
                                Visually secondary; only shown when the module is enabled. */}
                            {isGlobalSA && moduleEnabled && catCaps && (
                              <div className="mt-2 pt-2 border-t border-border/30 flex flex-wrap gap-1">
                                {catCaps.capabilities.map(cap => {
                                  const typeColors: Record<string, string> = {
                                    read:        "border-blue-400/60 text-blue-700 dark:text-blue-300",
                                    write:       "border-amber-400/60 text-amber-700 dark:text-amber-300",
                                    delete:      "border-red-400/60 text-red-700 dark:text-red-300",
                                    specialized: "border-purple-400/60 text-purple-700 dark:text-purple-300",
                                  };
                                  const cls = typeColors[cap.type] ?? typeColors.specialized;
                                  return (
                                    <button
                                      key={cap.key}
                                      type="button"
                                      title={`${cap.enabled ? "Disable" : "Enable"} "${cap.label}" capability for tenant organizations`}
                                      onClick={() => toggleCap.mutate({ category: group.category, capKey: cap.key, enabled: !cap.enabled })}
                                      disabled={toggleCap.isPending}
                                      className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-opacity ${cls} ${cap.enabled ? "opacity-100" : "opacity-40"}`}
                                      data-testid={`cap-toggle-${group.category}-${cap.key}`}
                                    >
                                      {cap.enabled ? null : <Lock className="w-2.5 h-2.5 mr-0.5 shrink-0" />}
                                      {cap.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* ── Tenant override (shown when a tenant is selected) ── */}
                            {selectedTenantId && tenantMod && (
                              <div className="mt-2 pt-2 border-t border-border/30">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
                                      <Building2 className="w-3 h-3 shrink-0" aria-hidden="true" />
                                      <span className="truncate" title={selectedTenantName}>
                                        {selectedTenantName ?? "Selected tenant"} override
                                      </span>
                                      {tenantMod.source === "override" ? (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 font-normal text-violet-700 border-violet-300 dark:text-violet-300 dark:border-violet-700">
                                          override set
                                        </Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 font-normal">
                                          following global
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      Effective for this tenant:{" "}
                                      <span className={tenantMod.effectiveEnabled
                                        ? "text-green-600 dark:text-green-400 font-medium"
                                        : "text-red-600 dark:text-red-400 font-medium"
                                      }>
                                        {tenantMod.effectiveEnabled ? "enabled" : "disabled"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <Switch
                                      checked={tenantMod.effectiveEnabled}
                                      onCheckedChange={val =>
                                        toggleTenantModule.mutate({ category: group.category, enabled: val })
                                      }
                                      disabled={toggleTenantModule.isPending}
                                      aria-label={`${tenantMod.effectiveEnabled ? "Disable" : "Enable"} ${group.category} for ${selectedTenantName}`}
                                      data-testid={`switch-tenant-module-${group.category}`}
                                    />
                                    {/* Clear override button — only when an override exists */}
                                    {tenantMod.source === "override" && (
                                      <button
                                        type="button"
                                        title="Clear override — revert to global setting"
                                        onClick={() => toggleTenantModule.mutate({ category: group.category, enabled: null })}
                                        disabled={toggleTenantModule.isPending}
                                        className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border/50 hover:border-border transition-colors"
                                        data-testid={`button-clear-tenant-module-${group.category}`}
                                      >
                                        <RotateCcw className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" />
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── SA global context: raw permission checkboxes ── */}
                        {isGlobalSA && (() => {
                          // Reusable inline renderer for a single permission checkbox row.
                          const PermRow = ({ p }: { p: (typeof group.items)[0] }) => {
                            const locked = isLocked(p.name);
                            return (
                              <label
                                key={p.name}
                                htmlFor={`checkbox-perm-${p.name}`}
                                className={`flex items-start gap-2 p-2 rounded border border-border ${locked ? "cursor-default opacity-60" : "hover:bg-muted/40 cursor-pointer"}`}
                                title={locked && !readOnly ? "Controlled by your platform administrator" : undefined}
                                data-testid={`label-perm-${p.name}`}
                              >
                                <Checkbox
                                  id={`checkbox-perm-${p.name}`}
                                  checked={draft?.permissions.has(p.name) ?? false}
                                  onCheckedChange={() => toggleOne(p.name)}
                                  disabled={locked}
                                  className="mt-0.5 shrink-0"
                                  data-testid={`checkbox-perm-${p.name}`}
                                />
                                <div className="min-w-0">
                                  <div className="text-xs font-medium leading-tight">{p.name}</div>
                                  {p.description && <div className="text-[10px] text-muted-foreground mt-0.5">{p.description}</div>}
                                </div>
                              </label>
                            );
                          };

                          // When the module is disabled, show a notice instead of checkboxes.
                          if (!moduleEnabled) {
                            return (
                              <div className="text-[11px] text-muted-foreground italic py-1 px-2">
                                Module is currently disabled for tenants — permissions are saved but have no effect.
                              </div>
                            );
                          }

                          // Compliance Center gets a nested FERPA / HIPAA / WCAG / Core layout.
                          if (group.category === "compliance") {
                            const ferpa = group.items.filter(p => p.name.startsWith("ferpa."));
                            const hipaa = group.items.filter(p => p.name.startsWith("hipaa."));
                            const wcag  = group.items.filter(p => p.name.startsWith("wcag.") || p.name.startsWith("ada."));
                            const core  = group.items.filter(p => !ferpa.includes(p) && !hipaa.includes(p) && !wcag.includes(p));
                            const Section = ({ label, items }: { label: string; items: typeof group.items }) =>
                              items.length === 0 ? null : (
                                <div className="mb-2">
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">{label}</div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                    {items.map(p => <PermRow key={p.name} p={p} />)}
                                  </div>
                                </div>
                              );
                            return (
                              <>
                                <Section label="Core access" items={core} />
                                <Section label="FERPA" items={ferpa} />
                                <Section label="HIPAA" items={hipaa} />
                                <Section label="ADA / WCAG" items={wcag} />
                              </>
                            );
                          }

                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                              {group.items.map(p => <PermRow key={p.name} p={p} />)}
                            </div>
                          );
                        })()}

                        {/* ── OrgAdmin or SA-in-org: capability rows ── */}
                        {!isGlobalSA && (() => {
                          const catCapsData = capMap.get(group.category);

                          if (capabilityLoading) return <Skeleton className="h-20 w-full" />;
                          if (capabilityError) return (
                            <div className="text-[11px] text-destructive py-1 px-2">
                              Could not load capability availability. Refresh the page to retry.
                            </div>
                          );
                          if (!catCapsData) return null;

                          // Compliance Center: split into FERPA + core sections.
                          if (group.category === "compliance") {
                            const ferpaPerms = group.items.filter(p => p.name.startsWith("ferpa.")).map(p => p.name);
                            const otherCaps  = catCapsData.capabilities.filter(c => !c.permissions.every(p => ferpaPerms.includes(p)));
                            const ferpaCaps  = catCapsData.capabilities.filter(c =>  c.permissions.some(p => ferpaPerms.includes(p)));

                            const CapRow = ({ cap }: { cap: CapabilityInfo }) => {
                              const platformDisabled = !cap.enabled;
                              return (
                                <label
                                  className={`flex items-start gap-2 p-2 rounded border border-border ${platformDisabled ? "cursor-default opacity-50" : readOnly ? "cursor-default" : "hover:bg-muted/40 cursor-pointer"}`}
                                  title={platformDisabled ? "Disabled by platform administrator" : undefined}
                                >
                                  <Checkbox
                                    checked={isCapChecked(cap.permissions)}
                                    onCheckedChange={checked => toggleCaps(cap.permissions, !!checked, cap.enabled)}
                                    disabled={platformDisabled || readOnly}
                                    className="mt-0.5 shrink-0"
                                  />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs font-medium leading-tight">{cap.label}</span>
                                      {platformDisabled && <span className="text-[9px] text-muted-foreground">(Platform disabled)</span>}
                                    </div>
                                  </div>
                                </label>
                              );
                            };
                            return (
                              <>
                                {otherCaps.length > 0 && (
                                  <div className="mb-2">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">Core access</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">{otherCaps.map(c => <CapRow key={c.key} cap={c} />)}</div>
                                  </div>
                                )}
                                {ferpaCaps.length > 0 && (
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">FERPA</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">{ferpaCaps.map(c => <CapRow key={c.key} cap={c} />)}</div>
                                  </div>
                                )}
                              </>
                            );
                          }

                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                              {catCapsData.capabilities.map(cap => {
                                const platformDisabled = !cap.enabled;
                                return (
                                  <label
                                    key={cap.key}
                                    className={`flex items-start gap-2 p-2 rounded border border-border ${platformDisabled ? "cursor-default opacity-50" : readOnly ? "cursor-default" : "hover:bg-muted/40 cursor-pointer"}`}
                                    title={platformDisabled ? "Disabled by platform administrator" : undefined}
                                  >
                                    <Checkbox
                                      checked={isCapChecked(cap.permissions)}
                                      onCheckedChange={checked => toggleCaps(cap.permissions, !!checked, cap.enabled)}
                                      disabled={platformDisabled || readOnly}
                                      className="mt-0.5 shrink-0"
                                    />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs font-medium leading-tight">{cap.label}</span>
                                        {platformDisabled && <span className="text-[9px] text-muted-foreground">(Platform disabled)</span>}
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* ── Create role dialog ──────────────────────────────────────────────── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create role</DialogTitle>
            <DialogDescription>A new custom role with no permissions granted. Add permissions after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-role-name">Name</Label>
              <Input id="new-role-name" value={newRole.name} onChange={e => setNewRole(r => ({ ...r, name: e.target.value }))} placeholder="e.g. Reviewer" className="mt-1" data-testid="input-new-role-name" />
            </div>
            <div>
              <Label htmlFor="new-role-desc">Description (optional)</Label>
              <Input id="new-role-desc" value={newRole.description} onChange={e => setNewRole(r => ({ ...r, description: e.target.value }))} placeholder="What this role is for" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!newRole.name.trim() || create.isPending} data-testid="button-confirm-new-role">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Clone role dialog ───────────────────────────────────────────────── */}
      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone "{current?.name}"</DialogTitle>
            <DialogDescription>Creates a new role with the same permissions as {current?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="clone-role-name">New role name</Label>
              <Input id="clone-role-name" value={cloneDraft.name} onChange={e => setCloneDraft(d => ({ ...d, name: e.target.value }))} className="mt-1" data-testid="input-clone-role-name" />
            </div>
            <div>
              <Label htmlFor="clone-role-desc">Description (optional)</Label>
              <Input id="clone-role-desc" value={cloneDraft.description} onChange={e => setCloneDraft(d => ({ ...d, description: e.target.value }))} placeholder="Describe the new role" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneOpen(false)}>Cancel</Button>
            <Button
              onClick={() => current && clone.mutate({ source: current.name, name: cloneDraft.name, description: cloneDraft.description })}
              disabled={!cloneDraft.name.trim() || clone.isPending}
              data-testid="button-confirm-clone-role"
            >
              Clone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
